import React, { useMemo, useState } from "react";

import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";

import ModernModal from "../../common/ModernModal";
import {
  ModernFormAlert,
  ModernFormField,
  ModernSubmitButton,
} from "../../common/ModernForm";
import { projectsService } from "../../Project/services";

interface CreatedProjectOption {
  id?: string | null;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onProjectCreated: (project: CreatedProjectOption) => void;
}

export default function CreateProjectDialog({
  open,
  onClose,
  onProjectCreated,
}: Props) {
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [validationError, setValidationError] = useState("");

  const {
    mutateAsync: createProject,
    isPending,
    isError,
    error,
    reset,
  } = useMutation({
    mutationKey: ["create-project-inline-upload"],
    mutationFn: () =>
      projectsService.createProject({
        project_name: projectName.trim(),
        project_description: projectDescription.trim(),
      }),
  });

  const serverErrorText = useMemo(() => {
    if (!isError) return "";
    const err = error as Record<string, any> | null;
    return (
      err?.message ||
      err?.error ||
      err?.response?.data?.message ||
      "Failed to create project. Please try again."
    );
  }, [error, isError]);

  const resetDialog = () => {
    setProjectName("");
    setProjectDescription("");
    setValidationError("");
    reset();
  };

  const handleClose = () => {
    if (isPending) return;
    resetDialog();
    onClose();
  };

  const handleCreate = async () => {
    if (!projectName.trim()) {
      setValidationError("Project name is required");
      return;
    }

    setValidationError("");
    const response = await createProject();
    const payload = (response?.project || response) as Record<string, any>;
    const createdId = payload?.id;
    const createdName = payload?.name || projectName.trim();

    onProjectCreated({ id: createdId ?? null, label: createdName });
    resetDialog();
    onClose();
  };

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Create New Project"
      maxWidth="sm"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {validationError && (
          <ModernFormAlert severity="error">{validationError}</ModernFormAlert>
        )}
        {serverErrorText && (
          <ModernFormAlert severity="error">{serverErrorText}</ModernFormAlert>
        )}

        <ModernFormField
          label="Project Name"
          name="project_name"
          value={projectName}
          onChange={(event) => {
            setProjectName(event.target.value);
            if (validationError) setValidationError("");
          }}
          placeholder="Enter a project name"
          required
        />

        <ModernFormField
          label="Project Description"
          name="project_description"
          multiline
          rows={3}
          value={projectDescription}
          onChange={(event) => setProjectDescription(event.target.value)}
          placeholder="Optional project description"
          helperText="Optional: add context for this project"
        />

        <ModernSubmitButton
          label="Create Project"
          loading={isPending}
          disabled={isPending || !projectName.trim()}
          onClick={handleCreate}
          fullWidth
        />
      </Box>
    </ModernModal>
  );
}
